/**
 * Static label map for known URL path segments.
 * Maps segment strings to human-readable breadcrumb labels.
 */
export const breadcrumbRegistry: Record<string, string> = {
  dashboard: 'Dashboard',
  surveys: 'Surveys',
  'survey-audits': 'Audits',
  reports: 'Reports',
  statistics: 'Statistics',
  manage: 'Manage',
  settings: 'Settings',
  'bug-report': 'Bug Report',
  universities: 'Universities',
  'system-admins': 'System Admins',
  institutes: 'Institutes',
  admins: 'Admins',
  users: 'Users',
  subjects: 'Subjects',
  'survey-templates': 'Survey Templates',
  questions: 'Questions',
  view: 'View',
  overview: 'Overview',
  'question-insights': 'Survey Insight',
};

/**
 * Dynamic segment resolution map.
 * When a segment is UUID-like, we look at the preceding segment to determine
 * which metadata keys to try for resolving the display label.
 * The first matching key wins.
 */
export const dynamicSegmentKeys: Record<string, string[]> = {
  universities: ['tenantCode', 'tenantName'],
  institutes: ['domainCode', 'domainName'],
  subjects: ['subjectCode', 'subjectName'],
  surveys: ['surveyTitle'],
  'survey-audits': ['auditTitle'],
  reports: ['reportTitle'],
  'survey-templates': ['templateTitle'],
};

const UUID_PATTERN = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

/**
 * Check if a segment looks like a UUID (dynamic segment).
 */
export const isUuidSegment = (segment: string): boolean => {
  return UUID_PATTERN.test(segment);
};

/**
 * Resolve a breadcrumb label for a given path segment.
 *
 * @param segment - The current path segment
 * @param precedingSegment - The segment before this one (used for dynamic resolution)
 * @param metadata - Breadcrumb metadata from context
 * @returns A human-readable label
 */
export const getBreadcrumbLabel = (
  segment: string,
  precedingSegment: string | undefined,
  metadata: Record<string, string | undefined>
): string => {
  // Check static registry first
  if (segment in breadcrumbRegistry) {
    return breadcrumbRegistry[segment];
  }

  // If it's a UUID-like segment, try dynamic resolution
  if (isUuidSegment(segment) && precedingSegment) {
    const keys = dynamicSegmentKeys[precedingSegment];
    if (keys) {
      for (const key of keys) {
        const value = metadata[key];
        if (value) {
          return value;
        }
      }
    }
    // Fallback to truncated ID
    return `${segment.substring(0, 8)}...`;
  }

  // Fallback: convert kebab-case to Title Case
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
